import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData } from "../../utility/Utility";
import CustomLoader from "../../Components/CustomLoader/CustomLoader";

export default function AgentMeetingLead() {
    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [statusFilter, setStatusFilter] = useState("all");
    const rowsPerPage = 10;

    const fetchMeetings = async (page = 1) => {
        setLoading(true);
        try {
            const response = await postData("api/meetings/admin/all-meeting", {
                page,
                limit: rowsPerPage,
                status: statusFilter === "all" ? undefined : statusFilter
            });

            if (response?.success) {
                setMeetings(response.data || []);
                setTotalPages(response.pagination?.totalPages || 0);
            } else {
                toast.error(response?.message || "Failed to fetch meetings");
            }
        } catch (error) {
            toast.error("Error fetching meetings");
            console.error("Fetch meetings error:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMeetings(currentPage + 1);
    }, [currentPage, statusFilter]);

    const handlePageChange = ({ selected }) => {
        setCurrentPage(selected);
    };

    const formatDateTime = (dateString) => {
        const options = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        };
        return new Date(dateString).toLocaleString('en-US', options);
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            scheduled: 'primary',
            completed: 'success',
            cancelled: 'danger',
            in_progress: 'info'
        };
        return statusMap[status] || 'secondary';
    };

    const getMeetingTypeLabel = (type) => {
        const typeMap = {
            policy_discussion: 'Policy Discussion',
            claim_settlement: 'Claim Settlement',
            new_business: 'New Business',
            renewal: 'Renewal',
            other: 'Other'
        };
        return typeMap[type] || type;
    };

    return (
        <div className="content-page">
            <CustomLoader loading={loading} />
            <ToastContainer position="top-right" autoClose={3000} />
            <div className="content">
                <div className="container-fluid">
                    {/* Breadcrumb */}
                    <div className="row">
                        <div className="col-12 py-2">
                            <div className="page-title-box py-2  d-sm-flex align-items-center justify-content-between">
                                <h4 className="mb-sm-0">Meeting Management</h4>

                            </div>

                            <div className="page-title-right">
                                <nav aria-label="breadcrumb">
                                    <ol className="breadcrumb m-0">
                                        <li className="breadcrumb-item">
                                            <Link to="/dashboard">Dashboard</Link>
                                        </li>
                                        <li className="breadcrumb-item active" aria-current="page">
                                            Agent Meetings
                                        </li>
                                    </ol>
                                </nav>
                            </div>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="row g-3 align-items-center">
                                        <div className="col-md-3">
                                            <label className="form-label">Status</label>
                                            <select
                                                className="form-select"
                                                value={statusFilter}
                                                onChange={(e) => {
                                                    setStatusFilter(e.target.value);
                                                    setCurrentPage(0);
                                                }}
                                            >
                                                <option value="all">All Status</option>
                                                <option value="scheduled">Scheduled</option>
                                                <option value="in_progress">In Progress</option>
                                                <option value="completed">Completed</option>
                                                <option value="cancelled">Cancelled</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Meetings Table */}
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    {loading ? (
                                        <div className="text-center py-5">
                                            <CustomLoader loading={loading} />
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-hover align-middle">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Title</th>
                                                        <th>Host</th>
                                                        <th> Participant Contact</th>
                                                        <th>Schedule</th>
                                                        <th>Type</th>
                                                        <th>Status</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {meetings.length > 0 ? (
                                                        meetings.map((meeting, index) => (
                                                            <tr key={meeting._id}>
                                                                <td>{currentPage * rowsPerPage + index + 1}</td>
                                                                <td>
                                                                    <div className="fw-medium">{meeting.title}</div>
                                                                    <small className="text-muted">{meeting.description}</small>
                                                                </td>
                                                                <td>
                                                                    {meeting.host?.fullName}
                                                                    <div className="text-muted small">{meeting.host?.email}</div>
                                                                </td>
                                                                <td>
                                                                    {meeting.participant ? (
                                                                        <div>
                                                                            <div>{meeting.participant.fullName}</div>
                                                                            {meeting.participant.email && (
                                                                                <div className="small">
                                                                                    <a href={`mailto:${meeting.participant.email}`} className="text-muted">
                                                                                        {meeting.participant.email}
                                                                                    </a>
                                                                                </div>
                                                                            )}
                                                                            {meeting.participant.phone && (
                                                                                <div className="small">
                                                                                    <a href={`tel:${meeting.participant.phone}`} className="text-muted">
                                                                                        {meeting.participant.phone}
                                                                                    </a>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <div>
                                                                            {meeting.contactInfo?.mobileNumber && (
                                                                                <div>
                                                                                    <a href={`tel:${meeting.contactInfo.mobileNumber}`} className="text-muted">
                                                                                        {meeting.contactInfo.mobileNumber}
                                                                                    </a>
                                                                                </div>
                                                                            )}
                                                                            {meeting.contactInfo?.email && (
                                                                                <div className="small">
                                                                                    <a href={`mailto:${meeting.contactInfo.email}`} className="text-muted">
                                                                                        {meeting.contactInfo.email}
                                                                                    </a>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <div>Start: {formatDateTime(meeting.schedule?.startTime)}</div>
                                                                    <div>End: {formatDateTime(meeting.schedule?.endTime)}</div>
                                                                    <small className="text-muted">
                                                                        {meeting.schedule?.timezone}
                                                                    </small>
                                                                </td>
                                                                <td>
                                                                    <span className="badge bg-light text-dark">
                                                                        {getMeetingTypeLabel(meeting.meetingType)}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    <span className={`badge bg-${getStatusBadge(meeting.status)}`}>
                                                                        {meeting.status}
                                                                    </span>
                                                                </td>
                                                                <td>
                                                                    {meeting.status === 'completed' && meeting.recordingUrl ? (
                                                                        <a
                                                                            href={meeting.recordingUrl}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="btn btn-sm btn-outline-success"
                                                                            download
                                                                        >
                                                                            <i className="mdi mdi-download"></i> Recording
                                                                        </a>
                                                                    ) : (
                                                                        <span className="text-muted">
                                                                            {meeting.status === 'cancelled' ? 'Cancelled' : 'Recording not available'}
                                                                        </span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="8" className="text-center py-4">
                                                                No meetings found
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>

                                            {totalPages > 1 && (
                                                <ReactPaginate
                                                    previousLabel={<i className="mdi mdi-chevron-left"></i>}
                                                    nextLabel={<i className="mdi mdi-chevron-right"></i>}
                                                    breakLabel="..."
                                                    breakClassName="page-item"
                                                    breakLinkClassName="page-link"
                                                    pageCount={totalPages}
                                                    marginPagesDisplayed={2}
                                                    pageRangeDisplayed={5}
                                                    onPageChange={handlePageChange}
                                                    containerClassName="pagination pagination-rounded justify-content-end mt-3"
                                                    pageClassName="page-item"
                                                    pageLinkClassName="page-link"
                                                    previousClassName="page-item"
                                                    previousLinkClassName="page-link"
                                                    nextClassName="page-item"
                                                    nextLinkClassName="page-link"
                                                    activeClassName="active"
                                                    forcePage={currentPage}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}